import { makeAutoObservable } from "mobx";
import { Bid } from "types/common/ServiceStoreTypes";
import { RootStore, ServiceStore } from "@intrasync/stores";

export class BidsListStore {
  rootStore: RootStore;

  // Observables
  loading = false;
  error: string | null = null;
  filtersVisible = true;

  // Constructor
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions
  toggleFiltersVisibility() {
    this.filtersVisible = !this.filtersVisible;
  }

  toggleFilters() {
    this.filtersVisible = !this.filtersVisible;
  }

  // Async actions
  async loadBids() {
    this.loading = true;
    this.error = null;

    try {
      // Load all relational data to ensure complete information is available
      await Promise.all([
        // Load all base entities without queries to populate stores
        this.bidsStore.find(),
        this.opportunitiesStore.find(),
        this.customersStore.find(),
        this.contactsStore.find(),
      ]);
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to load bids";
      console.error("Failed to load bids:", error);
    } finally {
      this.loading = false;
    }
  }

  async reloadBids() {
    await this.loadBids();
  }

  async createBid(bidData: Partial<Bid>) {
    try {
      const bid = (await this.bidsStore.create({
        ...bidData,
        CreatedAt: new Date().toISOString(),
        UpdatedAt: new Date().toISOString(),
      })) as unknown as Bid;

      // The service store will automatically update its entities
      return bid;
    } catch (error) {
      console.error("Failed to create bid:", error);
      throw error;
    }
  }

  async updateBid(bidId: number, updates: Partial<Bid>) {
    try {
      const updatedBid = (await this.bidsStore.patch(bidId, {
        ...updates,
        UpdatedAt: new Date().toISOString(),
      })) as unknown as Bid;

      // The service store will automatically update its entities
      return updatedBid;
    } catch (error) {
      console.error("Failed to update bid:", error);
      throw error;
    }
  }

  async deleteBid(bidId: number) {
    try {
      await this.bidsStore.remove(bidId);
      // The service store will automatically update its entities
      return true;
    } catch (error) {
      console.error("Failed to delete bid:", error);
      throw error;
    }
  }

  // Computed values
  get bidsStore(): ServiceStore {
    return this.rootStore.bidsStore as ServiceStore;
  }

  get opportunitiesStore(): ServiceStore {
    return this.rootStore.opportunitiesStore as ServiceStore;
  }

  get customersStore(): ServiceStore {
    return this.rootStore.customersStore as ServiceStore;
  }

  get contactsStore(): ServiceStore {
    return this.rootStore.contactsStore as ServiceStore;
  }

  get bids(): Bid[] {
    return this.bidsStore.entities as unknown[] as Bid[];
  }

  get hasFilters(): boolean {
    return false; // Filters are now handled by the datagrid
  }

  get availableStatuses(): string[] {
    return [
      ...new Set(this.bids.map((bid: any) => bid.status).filter(Boolean)),
    ].sort();
  }

  get bidMetrics() {
    const totalBids = this.bids.length;
    const totalValue = this.bids.reduce(
      (sum, bid) => sum + (bid.bidAmount || 0),
      0
    );
    const averageBidAmount = totalBids > 0 ? totalValue / totalBids : 0;

    const statusCounts = this.bids.reduce((acc, bid) => {
      acc[bid.status] = (acc[bid.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalBids,
      totalValue,
      averageBidAmount,
      statusCounts,
    };
  }
}

export default BidsListStore;
